import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Autocomplete, TextField, Grid, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { debounce } from '@mui/material/utils';
import { Translate } from '../../shared/LanguageService';

interface MainTextMatchedSubstrings {
    offset: number;
    length: number;
}

interface StructuredFormatting {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
    description: string;
    structured_formatting: StructuredFormatting;
    place_id: string;
    matched_substrings: MainTextMatchedSubstrings[];
    terms: MainTextMatchedSubstrings[];
    types: string[];
    distance_meters: number;
}

interface GooglePlacesAutocompleteProps {
    onSelect: (place: PlaceType | null) => void;
}

/** 
 * to use this component, you need to add the following script tag to your index.(html|ejs) file
 *   <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY&libraries=places"></script>
 */
const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({ onSelect }) => {
    const [value, setValue] = useState<PlaceType | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<readonly PlaceType[]>([]);
    const [loading, setLoading] = useState(true);
    const { site } = window.appConfig;

    const autocompleteService = useRef<any>(null);
    const placesService = useRef<any>(null);

    useEffect(() => {
        const initializeAutocompleteService = () => {
            if (window.google && window.google.maps && window.google.maps.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
                setLoading(false);
            } else {
                console.error('Google Maps API AutocompleteService is not initialized');
            }
        };

        if (window.google && window.google.maps && window.google.maps.places) {
            initializeAutocompleteService();
        } else {
            window.addEventListener('load', initializeAutocompleteService);
        }
    }, []);

    const fetch = useMemo(
        () =>
            debounce(
                (
                    request: { input: string },
                    callback: (results?: readonly PlaceType[]) => void,
                ) => {
                    if (autocompleteService.current) {
                        (autocompleteService.current as any).getPlacePredictions(
                            {
                                ...request,
                                componentRestrictions: { country: [getCountryCode()] },
                            },
                            (results: readonly PlaceType[]) => {
                                callback(results);
                            },
                        );
                    } else {
                        console.error('AutocompleteService is not initialized');
                    }
                },
                700,
            ),
        [],
    );

    useEffect(() => {
        if (inputValue && !loading) {
            fetch({ input: inputValue }, (results) => {
                setOptions(results || []);
            });
        }
    }, [inputValue, loading, fetch]);

    const getCountryCode = () => site === 'zentuvo' ? 'fi' : 'se';

    const fetchPlaceDetails = (placeId: string): Promise<PlaceType> => {
        return new Promise((resolve, reject) => {
            if (placesService.current) {
                placesService.current.getDetails({ placeId }, (place, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                        resolve(place as PlaceType);
                    } else {
                        reject(status);
                    }
                });
            } else {
                reject('PlacesService is not initialized');
            }
        });
    };

    const handleOptionSelect = async (event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if (newValue) {
            try {
                const placeDetails = await fetchPlaceDetails(newValue.place_id);
                onSelect(placeDetails);
            } catch (error) {
                console.error('Error fetching place details:', error);
                onSelect(null);
            }
        } else {
            onSelect(null);
        }
    };

    const getHighlightedText = (text: string, highlight: string) => {
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        return (
            <span>
                {parts.map((part, index) =>
                    part.toLowerCase() === highlight.toLowerCase() ? (
                        <span key={index} style={{ fontWeight: 'bold' }}>
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    return (
        <Autocomplete
            id="google-places-api-autocomplete"
            sx={{ width: '100%' }}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
            }
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            noOptionsText={Translate('components/common/google-places-autocomplete/no-options-text')}
            onChange={handleOptionSelect}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
            renderInput={(params) => (
                <TextField {...params} label={Translate('components/common/google-places-autocomplete/label')} fullWidth variant="standard" />
            )}
            renderOption={(props, option) => {
                const optionProps = props;
                const query = inputValue;

                return (
                    <li key={option.description} {...optionProps}>
                        <Grid container sx={{ alignItems: 'center' }}>
                            <Grid item sx={{ display: 'flex', width: 44 }}>
                                <LocationOnIcon sx={{ color: 'text.secondary' }} />
                            </Grid>
                            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                {getHighlightedText(option.structured_formatting.main_text, query)}
                                <Typography variant="body2" color="text.secondary">
                                    {option.structured_formatting.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};

export default GooglePlacesAutocomplete;
